import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Archive, ArrowRight, AtSign, Mail, Phone, User } from "lucide-react";
import { api, qs } from "@/lib/api";
import { hasOp } from "@/lib/stubs";
import { FORUM_NAMES, type ForumAccount } from "@/features/contacts/ForumsCard";
import type { SearchHit } from "@/lib/types";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./ui/command";
import { Badge } from "./ui/badge";

/** Debounce typing so each keystroke doesn't fire a search (every search is audited). */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function useContactSearch(q: string) {
  const term = useDebounced(q.trim(), 120);
  return useQuery({
    queryKey: ["search", term],
    queryFn: () => api<{ hits: SearchHit[] }>("/api/v1/search/contacts" + qs({ q: term, limit: 20 })),
    enabled: term.length > 0,
    placeholderData: (prev) => prev,
    staleTime: 30_000,
  });
}

/** Forum accounts whose username starts with the text (one word, 2+ characters; "@name" works too). */
export function useForumSearch(q: string) {
  const term = useDebounced(q.trim().replace(/^@/, ""), 200);
  const available = useQuery({ queryKey: ["has-op", "forums_search"], queryFn: () => hasOp("forums_search"), staleTime: Infinity });
  const ok = available.data === true && term.length >= 2 && !/\s/.test(term);
  return useQuery({
    queryKey: ["forum-search", term],
    queryFn: () => api<{ accounts: ForumAccount[] }>("/api/v1/forums/accounts" + qs({ username: term, limit: 6 })),
    enabled: ok,
    staleTime: 30_000,
    select: (r) => (ok ? r.accounts : []),
  });
}

const PAGES = [
  { to: "/contacts", label: "Contacts" },
  { to: "/contacts/duplicates", label: "Possible duplicates" },
  { to: "/events", label: "Events and rosters" },
  { to: "/events/sales", label: "Sales after an event (webinar to sale)" },
  { to: "/automations", label: "Automations" },
  { to: "/forms", label: "Website forms and spam" },
  { to: "/email/broadcasts", label: "Broadcasts" },
  { to: "/email/reports", label: "Email reports" },
  { to: "/email/segments", label: "Segments" },
  { to: "/email/suppressions", label: "Blocked addresses (suppressions)" },
  { to: "/email/warmup", label: "IP warm-up" },
  { to: "/email/gmail", label: "Gmail log (Sync Gmail now)" },
  { to: "/billing/approvals", label: "Billing approvals" },
  { to: "/billing/delivery-problems", label: "Paid, but something is missing" },
  { to: "/billing/orders", label: "Orders and payments (refunds)" },
  { to: "/billing/subscriptions", label: "Subscriptions" },
  { to: "/billing/products", label: "Products and prices" },
  { to: "/billing/promotions", label: "Promotions and codes" },
  { to: "/affiliates", label: "Affiliates (partners, commissions, payouts)" },
  { to: "/billing/card-links", label: "Card links report (go-live)" },
  { to: "/billing/club-cards", label: "Club cards to enter in Stripe (go-live)" },
  { to: "/settings", label: "Settings" },
] as const;

/** Cmd-K: current contacts first, then the archive; with nothing typed, page shortcuts. */
export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const search = useContactSearch(q);
  const forum = useForumSearch(q);
  const hits = q.trim() ? (search.data?.hits ?? []) : [];
  const forumHits = q.trim() ? (forum.data ?? []) : [];
  const current = hits.filter((h) => !h.archived);
  const archived = hits.filter((h) => h.archived);
  const go = (id: number) => {
    onOpenChange(false);
    setQ("");
    navigate({ to: "/contacts/$contactId", params: { contactId: String(id) } });
  };
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <DialogPrimitive.Content className="fixed left-1/2 top-[10vh] z-50 w-[min(640px,calc(100vw-2rem))] -translate-x-1/2 overflow-hidden rounded-[var(--radius-md)] border bg-[var(--color-card)] shadow-xl">
          <DialogPrimitive.Title className="sr-only">Search contacts</DialogPrimitive.Title>
          <Command shouldFilter={false} label="Search contacts">
            <CommandInput autoFocus value={q} onValueChange={setQ} placeholder="Name, email, phone, company, ID or forum username…" />
            <CommandList>
              {q.trim() && !search.isFetching && !forum.isFetching && hits.length === 0 && forumHits.length === 0 && <CommandEmpty>No contacts found.</CommandEmpty>}
              {current.length > 0 && (
                <CommandGroup heading="Contacts">
                  {current.map((h) => (
                    <HitRow key={h.contact_id} hit={h} onSelect={() => go(h.contact_id)} />
                  ))}
                </CommandGroup>
              )}
              {forumHits.length > 0 && (
                <CommandGroup heading="Forum usernames">
                  {forumHits.map((a) => (
                    <CommandItem
                      key={`${a.forum}-${a.username}`}
                      value={`forum-${a.forum}-${a.username}`}
                      onSelect={() => {
                        if (a.contact_id) go(a.contact_id);
                        else {
                          window.open(a.profile_url, "_blank", "noopener");
                          onOpenChange(false);
                        }
                      }}
                    >
                      <AtSign className="size-4 text-[var(--color-muted-foreground)]" />
                      <div className="min-w-0 flex-1 truncate">
                        <span className="font-medium">{a.username}</span>
                        <span className="text-[var(--color-muted-foreground)]"> on the {FORUM_NAMES[a.forum] ?? a.forum}</span>
                      </div>
                      <span className="text-xs text-[var(--color-muted-foreground)]">
                        {a.contact_id ? `#${a.contact_id}` : a.match_status === "ambiguous" ? "several contacts share the email" : "no contact (opens the forum profile)"}
                      </span>
                      {a.gone && <Badge variant="muted">gone</Badge>}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {!q.trim() && (
                <CommandGroup heading="Go to">
                  {PAGES.map((p) => (
                    <CommandItem
                      key={p.to}
                      value={p.label}
                      onSelect={() => {
                        onOpenChange(false);
                        navigate({ to: p.to });
                      }}
                    >
                      <ArrowRight className="size-4 text-[var(--color-muted-foreground)]" />
                      {p.label}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {archived.length > 0 && (
                <CommandGroup heading="Archive (reference only)">
                  {archived.map((h) => (
                    <HitRow key={h.contact_id} hit={h} onSelect={() => go(h.contact_id)} />
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function HitRow({ hit, onSelect }: { hit: SearchHit; onSelect: () => void }) {
  return (
    <CommandItem value={String(hit.contact_id)} onSelect={onSelect}>
      {hit.archived ? <Archive className="size-4 text-[var(--color-muted-foreground)]" /> : <User className="size-4" />}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium">
          {hit.name || "(no name)"}
          {hit.nickname ? <span className="text-[var(--color-muted-foreground)]"> “{hit.nickname}”</span> : null}
          {hit.company ? <span className="text-[var(--color-muted-foreground)]"> · {hit.company}</span> : null}
        </div>
        <div className="flex gap-3 truncate text-xs text-[var(--color-muted-foreground)]">
          {hit.email && (
            <span className="flex items-center gap-1">
              <Mail className="size-3" />
              {hit.email}
            </span>
          )}
          {hit.phone && (
            <span className="flex items-center gap-1">
              <Phone className="size-3" />
              {hit.phone}
            </span>
          )}
        </div>
      </div>
      <span className="text-xs text-[var(--color-muted-foreground)]">#{hit.contact_id}</span>
      {hit.archived && <Badge variant="muted">archived</Badge>}
    </CommandItem>
  );
}
